import {Pagination, PaginationParams} from '@src/models/pagination';
import {api} from './client';
import {
  EventDetails,
  EventItemInList,
  EventParticipant,
} from '@src/models/event';
import {ClubThatIsInMyEvents} from '@src/models/ClubThatIsInMyEvents';
import {Event} from '@src/models/voting';

export namespace EventsApi {
  interface GetMyEventsParams extends PaginationParams {
    companyIds?: string[];
    search?: string;
  }
  export const getMyEvents = async (params: GetMyEventsParams) => {
    const res = await api.get<Pagination<Event>>('events/joined', {
      params: {
        companyId: params.companyIds || undefined,
        page: params.page || undefined,
        limit: params.limit || undefined,
        search: params.search || undefined,
      },
    });

    return res.data;
  };

  export const getClubsThatAreInMyEvents = async () => {
    const res = await api.get<ClubThatIsInMyEvents[]>(
      'events/joined-company-names'
    );
    return res.data;
  };

  export interface createEventReq {
    date: string;
    endDate?: string;
    locationName?: string;
    description: string;
    address: {
      address: string;
      zipCode: string;
      city: string;
      country: string;
      latitude: string;
      longitude: string;
    };
    eventFee?: number;
    theme: string;
    onlyForMembers: boolean;
    currency?: string;
    minimumNumberOfPeople: number;
    maximumNumberOfPeople: number;
  }

  export const createEvent = async (id: string, body: createEventReq) => {
    const res = await api.post('events/new-event/' + id, body);
    return res.data;
  };

  interface getAllCreatedEventsParams extends PaginationParams {
    search: string;
    companyId: string;
  }

  export const getAllCreatedEvents = async (
    params: getAllCreatedEventsParams
  ) => {
    const res = await api.get<Pagination<EventItemInList>>(
      'events/' + params.companyId,
      {
        params: {
          page: params.page || undefined,
          limit: params.limit || undefined,
          query: params.search || undefined,
        },
      }
    );
    return res.data;
  };

  export const getEventDetailsById = async (id: string) => {
    const res = await api.get<EventDetails>('events/details/' + id);
    return res.data;
  };

  export const sendEventImage = async (formData: FormData) => {
    const res = await api.post<EventDetails>('cdn/upload', formData);
    return res.data;
  };

  export const editEvent = async (eventId: string, body: createEventReq) => {
    const res = await api.put<EventDetails>('events/edit/' + eventId, body);
    return res.data;
  };

  export const deleteEvent = async (eventId: string) => {
    const res = await api.delete('events/delete/' + eventId);
    return res.data;
  };

  export interface JoinEvent {
    actionType: boolean;
    eventId: string;
  }

  export const joinEvent = async (body: JoinEvent) => {
    const res = await api.post('events/join-or-leave-event', body);
    return res.data;
  };

  export type GetCreatedEventsParams = PaginationParams;
  export const getCreatedEvents = async (
    params: GetCreatedEventsParams
  ): Promise<Pagination<Event>> => {
    const {data} = await api.get<Pagination<Event>>('events/created', {params});
    return data;
  };

  export interface GetAllEventsParams extends PaginationParams {
    latitude?: number;
    longitude?: number;
    sort_by?: 'distance_DESC';
    searchArea?: number;
  }
  export const getAllEvents = async (
    params: GetAllEventsParams
  ): Promise<Pagination<Event>> => {
    const {data} = await api.get<Pagination<Event>>('events', {params});
    return data;
  };
  export const uploadFileToEvent = async (body: FormData) => {
    const res = await api.post('cdn/upload', body);
    return res.data;
  };
  export const deleteFile = async (id: string) => {
    const res = await api.delete('cdn/delete/' + id);
    return res.data;
  };
  export const getFile = async (id: string) => {
    const res = await api.get('cdn/files/' + id, {responseType: 'blob'});
    return res.data;
  };
  export const getInterests = async () => {
    const res = await api.get('interests');
    return res.data;
  };
  export const getVotesListForEvent = async (eventId: string) => {
    const res = await api.get('votes/votes-list-for-event/' + eventId);
    return res.data;
  };
  export const setEventPresence = async (eventId: string) => {
    const res = await api.put('events/visited/' + eventId);
    return res.data;
  };

  export const setParticipantPresence = async (
    eventId: string,
    userId: string
  ) => {
    const res = await api.put(`events/visited/${eventId}/user/${userId}`);
    return res.data;
  };

  export const removeParticipantPresence = async (
    eventId: string,
    userId: string
  ) => {
    const res = await api.delete(`events/visited/${eventId}/user/${userId}`);
    return res.data;
  };

  type GetEventParticipantsListParams = PaginationParams;

  export const getEventParticipantsList = async (
    eventId: string,
    params: GetEventParticipantsListParams
  ) => {
    const res = await api.get<Pagination<EventParticipant>>(
      `events/${eventId}/participants`,
      {params}
    );
    return res.data;
  };
  /** Printable A4 poster with the event's presence QR code (language from Accept-Language). */
  export const getEventQrPosterPdf = async (eventId: string, companyId: string) => {
    const res = await api.get<Blob>(
      `events/${eventId}/companies/${companyId}/qr-code/pdf`,
      {responseType: 'blob'}
    );
    return res.data;
  };
}
